'use client';

import * as Popover from '@radix-ui/react-popover';
import { Menu, X } from 'lucide-react';
import {
	type RefObject,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { cn } from '@/shared/utils';

import type { useCommandMenu } from './use-command-menu';

type ChatCommandMenuProps = {
	menu: ReturnType<typeof useCommandMenu>;
	anchorRef: RefObject<HTMLDivElement | null>;
	sideOffset?: number;
	label?: string;
	className?: string;
};

export function ChatCommandMenu({
	menu,
	anchorRef,
	sideOffset = 12,
	label,
	className,
}: ChatCommandMenuProps) {
	const { open, mode, items, selected, listId } = menu;
	const list = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLButtonElement | null>(null);
	const [placement, setPlacement] = useState({ width: 360, offset: 0 });

	// Поповер занимает всю ширину якоря, а не кнопки: сдвигаем его на разницу левых краёв.
	useLayoutEffect(() => {
		const anchor = anchorRef.current;
		const button = trigger.current;
		if (!open || !anchor || !button) return;
		const measure = () => {
			const rect = anchor.getBoundingClientRect();
			setPlacement({
				width: rect.width,
				offset: rect.left - button.getBoundingClientRect().left,
			});
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(anchor);
		window.addEventListener('resize', measure);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, [anchorRef, open]);

	useEffect(() => {
		if (!open) return;
		document
			.getElementById(`${listId}-${selected}`)
			?.scrollIntoView({ block: 'nearest' });
	}, [listId, open, selected]);

	return (
		<Popover.Root open={open} onOpenChange={menu.openMenu}>
			<Popover.Trigger asChild>
				<button
					ref={(node) => {
						trigger.current = node;
						menu.setTrigger(node);
					}}
					type="button"
					aria-label={label ?? 'Меню команд'}
					title={label ?? 'Меню команд'}
					className={cn(
						'flex size-8 shrink-0 items-center justify-center rounded-lg text-gray-3 transition-colors hover:bg-gray-5 hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent active:bg-gray-4/50 data-[state=open]:bg-gray-5 data-[state=open]:text-black',
						className,
					)}
				>
					{open ? (
						<X
							size={18}
							strokeWidth={1.8}
							className="shrink-0"
							aria-hidden="true"
						/>
					) : (
						<Menu
							size={18}
							strokeWidth={1.8}
							className="shrink-0"
							aria-hidden="true"
						/>
					)}
					{label && <span>{label}</span>}
				</button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content
					side="top"
					align="start"
					alignOffset={placement.offset}
					sideOffset={sideOffset}
					collisionPadding={12}
					style={{ width: placement.width }}
					aria-label={label ?? 'Меню команд'}
					onOpenAutoFocus={(event) => {
						event.preventDefault();
						// В slash-режиме фокус остаётся в поле ввода.
						if (mode === 'button') list.current?.focus({ preventScroll: true });
					}}
					onCloseAutoFocus={menu.onCloseAutoFocus}
					onInteractOutside={(event) => {
						if (
							mode === 'slash' &&
							event.target instanceof Element &&
							event.target.closest('[data-dialog-input], [data-dialog-submit]')
						)
							event.preventDefault();
					}}
					className="z-50 flex max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-2xl border border-gray-4/70 bg-white text-sm shadow-listbox"
				>
					<div
						ref={list}
						id={listId}
						role="listbox"
						tabIndex={mode === 'button' ? 0 : -1}
						aria-label="Команды диалога"
						aria-activedescendant={
							items.length ? `${listId}-${selected}` : undefined
						}
						onKeyDown={menu.keyDown}
						className={cn(
							'group min-h-0 overflow-y-auto outline-none',
							items.length > 0 && 'p-1',
						)}
					>
						{items.map((item, index) => (
							<button
								key={item.id}
								type="button"
								id={`${listId}-${index}`}
								role="option"
								aria-selected={index === selected}
								aria-disabled={item.disabled || undefined}
								aria-label={`${item.label} ${item.command}`}
								title={`${item.command} — ${item.description ?? item.label}`}
								tabIndex={-1}
								onPointerMove={() => menu.setActive(index)}
								onPointerDown={(event) => event.preventDefault()}
								onClick={() => void menu.select(item)}
								className={cn(
									'flex min-h-8 w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left leading-5 transition-colors aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
									index === selected &&
										'bg-gray-5 group-focus-visible:ring-1 group-focus-visible:ring-inset group-focus-visible:ring-gray-4',
								)}
							>
								<item.icon
									size={16}
									strokeWidth={1.8}
									className="shrink-0 text-gray-3"
									aria-hidden="true"
								/>
								<span className="shrink-0 text-black">{item.label}</span>
								<span className="min-w-0 truncate text-gray-3">
									{item.description}
								</span>
							</button>
						))}
					</div>
					{!items.length && (
						<p role="status" className="px-3 py-5 text-gray-3">
							{mode === 'slash'
								? 'Команда не найдена. Enter отправит текст как сообщение'
								: 'Команд пока нет'}
						</p>
					)}
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
}
